import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton } from '@/ui/components/header';
import { Icon } from '@/ui/components/icon';
import { AiBrain02Icon, Delete02Icon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded';
import { MainScrollArea } from '@/ui/components/main';
import { useState } from 'react';
import { useAuth } from '@/features/auth';
import {
  PROJECT_TYPE_DESCRIPTIONS_TEXT,
  PROJECT_TYPES,
  ProjectType,
} from '@/features/projects/constants';
import { useAtom, atom } from 'jotai';
import {
  Field,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/ui/components/form/field';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectTriggerIcon,
  SelectValue,
} from '@/ui/components/select';
import { Button } from '@/ui/components/button';
import { toastManager } from '@/ui/components/toast';
import { Progress, ProgressTrack } from '@/ui/components/progress';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupItemText,
  RadioGroupItemTitle,
  RadioGroupLabel,
} from '@/ui/components/radio-group';
import { Fieldset } from '@/ui/components/form/fieldset';
import { FieldItem } from '@/ui/components/form/field';
import { FileDropzone } from '@/features/documents/components/file-upload/file-dropzone';
import { FileTable, FileRow } from '@/features/documents/components/file-table';
import { LanguageSelect } from '@/features/i18n/components/language-select';
import { companiesQuery } from '@/features/companies/queries';
import { useCreateTenderFeedbacks } from '@/features/tender-feedbacks/mutations';
import type { TenderFeedbackEntry } from '@/features/tender-feedbacks/types';
import { useSuspenseQuery } from '@tanstack/react-query';
import { CompanyCombobox } from '@/features/companies/components/company-combobox';
import { Textarea } from '@/ui/components/textarea';
import { Form } from '@/ui/components/form';

const emptyEntry = (): TenderFeedbackEntry => ({
  id: crypto.randomUUID(),
  tenderName: '',
  projectName: '',
  clientId: null,
  projectType: PROJECT_TYPES[0],
  isWon: null,
  tenderPackageFiles: [],
  bidFiles: [],
  feedbackFiles: [],
  feedbackText: '',
});

const FILE_FIELDS = [
  {
    key: 'tenderPackageFiles',
    label: { en: 'Tender package documents', nb: 'Anbudspakkedokumenter' },
  },
  { key: 'bidFiles', label: { en: 'Bid documents', nb: 'Tilbudsdokumenter' } },
  {
    key: 'feedbackFiles',
    label: { en: 'Feedback documents', nb: 'Tilbakemeldingsdokumenter' },
  },
] as const;

const languageAtom = atom<string | null>(null);
const entriesAtom = atom<TenderFeedbackEntry[]>([emptyEntry()]);

export const Route = createFileRoute('/_authed/settings/knowledge-base/new-feedback')({
  component: RouteComponent,
  loader: async ({ context }) => {
    await context.queryClient.query({
      ...companiesQuery(context.auth.user.organizationId),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const createFeedbacks = useCreateTenderFeedbacks();

  const [language, setLanguage] = useAtom(languageAtom);
  const [entries, setEntries] = useAtom(entriesAtom);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  return (
    <>
      <Header>
        <HeaderBackButton />
        <Breadcrumb>
          <BreadcrumbLink to="/settings/knowledge-base">
            <Icon icon={AiBrain02Icon} />
            {t({ en: 'Knowledge base', nb: 'Kunnskapsbase' })}
          </BreadcrumbLink>
          <BreadcrumbPage>
            {t({ en: 'Add past tenders', nb: 'Legg til tidligere anbud' })}
          </BreadcrumbPage>
        </Breadcrumb>
      </Header>
      <MainScrollArea>
        <Form
          onFormSubmit={() => {
            if (!language)
              return toastManager.add({
                type: 'error',
                title: t({ en: 'Language is required', nb: 'Språk er påkrevd' }),
              });

            for (const entry of entries) {
              if (!entry.clientId || !entry.isWon)
                return toastManager.add({
                  type: 'error',
                  title: t({
                    en: 'Please fill all required fields for each tender',
                    nb: 'Vennligst fyll ut alle obligatoriske felt for hvert anbud',
                  }),
                });

              if (entry.tenderPackageFiles.length === 0)
                return toastManager.add({
                  type: 'error',
                  title: t({
                    en: 'Tender package documents are required',
                    nb: 'Anbudspakkedokumenter er påkrevd',
                  }),
                });

              if (entry.bidFiles.length === 0)
                return toastManager.add({
                  type: 'error',
                  title: t({
                    en: 'Bid documents are required',
                    nb: 'Tilbudsdokumenter er påkrevd',
                  }),
                });

              if (entry.feedbackFiles.length === 0 && !entry.feedbackText.trim())
                return toastManager.add({
                  type: 'error',
                  title: t({
                    en: 'Feedback documents or written feedback is required',
                    nb: 'Tilbakemeldingsdokumenter eller skriftlig tilbakemelding er påkrevd',
                  }),
                });
            }

            setUploadProgress({ current: 0, total: entries.length });
            createFeedbacks.mutate(
              {
                entries,
                language,
                onProgress: (current) => setUploadProgress({ current, total: entries.length }),
              },
              {
                onSuccess: () => {
                  setEntries([emptyEntry()]);
                  navigate({ to: '/settings/knowledge-base', search: { tab: 'tender_feedbacks' } });
                },
              },
            );
          }}
        >
          <div className="px-x space-y-6 pt-4 pb-8">
            <Field>
              <FieldLabel>{t({ en: 'Language', nb: 'Språk' })}</FieldLabel>
              <LanguageSelect
                required
                value={language}
                onValueChange={setLanguage}
                disabled={createFeedbacks.isPending}
                placeholder={t({ en: 'Select language', nb: 'Velg språk' })}
              />
              <FieldDescription>
                {t({
                  en: 'This language will be applied to all tenders being uploaded.',
                  nb: 'Dette språket vil bli brukt for alle anbud som lastes opp.',
                })}
              </FieldDescription>
              <FieldError />
            </Field>
            <div className="text-sm text-muted">
              {t({
                en: 'For each tender, upload the tender package, submitted bid, and client or internal feedback. All tenders are uploaded together.',
                nb: 'For hvert anbud laster du opp anbudspakken, innsendt tilbud og tilbakemeldinger fra kunde eller interne evalueringer. Alle anbud lastes opp sammen.',
              })}
            </div>
            <div className="space-y-4">
              {entries.map((entry, index) => (
                <FeedbackEntryCard
                  key={entry.id}
                  entry={entry}
                  index={index}
                  onUpdate={(updates) =>
                    setEntries(
                      entries.map((item) =>
                        item.id === entry.id ? { ...item, ...updates } : item,
                      ),
                    )
                  }
                  onRemove={() => setEntries(entries.filter((item) => item.id !== entry.id))}
                  canRemove={entries.length > 1}
                  isSubmitting={createFeedbacks.isPending}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3 py-4 px-x z-50 border-t sticky bottom-0 bg-white">
            {createFeedbacks.isPending ? (
              <Progress
                value={(uploadProgress.current / uploadProgress.total) * 100}
                className="flex items-center gap-2 w-full"
              >
                <ProgressTrack />
                <span className="text-sm text-muted shrink-0 tabular-nums">
                  {uploadProgress.current} / {uploadProgress.total}
                </span>
              </Progress>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEntries([...entries, emptyEntry()])}
                kind="with-icon"
              >
                <Icon icon={PlusSignIcon} />
                {t({ en: 'Add another tender', nb: 'Legg til flere anbud' })}
              </Button>
            )}
            <div className="flex-1" />
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate({ to: '/settings/knowledge-base' })}
              disabled={createFeedbacks.isPending}
            >
              {t({ en: 'Cancel', nb: 'Avbryt' })}
            </Button>
            <Button type="submit" pending={createFeedbacks.isPending}>
              {t({
                en:
                  entries.length === 0
                    ? 'Upload tenders'
                    : entries.length === 1
                      ? `Upload (${entries.length}) tender`
                      : `Upload (${entries.length}) tenders`,
                nb: `Last opp ${entries.length === 0 ? 'anbud' : entries.length === 1 ? `(${entries.length}) anbud` : `(${entries.length}) anbud`}`,
              })}
            </Button>
          </div>
        </Form>
      </MainScrollArea>
    </>
  );
}

function FeedbackEntryCard({
  entry,
  index,
  onUpdate,
  onRemove,
  canRemove,
  isSubmitting,
}: {
  entry: TenderFeedbackEntry;
  index: number;
  onUpdate: (updates: Partial<TenderFeedbackEntry>) => void;
  onRemove: () => void;
  canRemove: boolean;
  isSubmitting: boolean;
}) {
  const auth = useAuth();

  const { data: clients } = useSuspenseQuery(companiesQuery(auth.user.organizationId));

  const selectedClient = clients.find((c) => c.id === entry.clientId) ?? null;

  const statusOptions = [
    { value: 'true', label: t({ en: 'Won', nb: 'Vunnet' }) },
    { value: 'false', label: t({ en: 'Lost', nb: 'Tapt' }) },
  ];

  return (
    <div className="border rounded-lg p-4 bg-white relative">
      {canRemove && (
        <Button
          variant="ghost"
          size="sm"
          kind="icon"
          type="button"
          className="absolute top-2 right-2"
          onClick={onRemove}
          disabled={isSubmitting}
          aria-label={t({ en: 'Remove', nb: 'Fjern' })}
        >
          <Icon icon={Delete02Icon} className="size-4" />
        </Button>
      )}
      <h4 className="font-medium mb-4">
        {t({ en: 'Tender', nb: 'Anbud' })} #{index + 1}
      </h4>
      <div className="space-y-4">
        <Field>
          <FieldLabel>{t({ en: 'Project name', nb: 'Prosjektnavn' })}</FieldLabel>
          <FieldControl
            required
            placeholder={t({ en: 'Project name', nb: 'Prosjektnavn' })}
            value={entry.projectName}
            onChange={(e) => onUpdate({ projectName: e.target.value })}
            disabled={isSubmitting}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>{t({ en: 'Project type', nb: 'Prosjekttype' })}</FieldLabel>
          <Select
            required
            value={entry.projectType}
            onValueChange={(v) => onUpdate({ projectType: v as ProjectType })}
            disabled={isSubmitting}
          >
            <SelectTrigger size="lg" className="w-full">
              <SelectValue>
                {(v) =>
                  v
                    ? t(common.projectType[v as ProjectType])
                    : t({ en: 'Select project type', nb: 'Velg prosjekttype' })
                }
              </SelectValue>
              <SelectTriggerIcon />
            </SelectTrigger>
            <SelectPopup>
              {PROJECT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(common.projectType[type])}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
          <FieldError />
          <FieldDescription>
            {t(PROJECT_TYPE_DESCRIPTIONS_TEXT[entry.projectType])}
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel>
            {t({ en: 'Workspace name (Tender)', nb: 'Arbeidsområdenavn (Anbud)' })}
          </FieldLabel>
          <FieldControl
            required
            placeholder={t({ en: 'Workspace name (Tender)', nb: 'Arbeidsområdenavn (Anbud)' })}
            value={entry.tenderName}
            onChange={(e) => onUpdate({ tenderName: e.target.value })}
            disabled={isSubmitting}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>{t({ en: 'Client', nb: 'Klient' })}</FieldLabel>
          <CompanyCombobox
            required
            companies={clients}
            value={selectedClient}
            onValueChange={(company) => onUpdate({ clientId: company?.id ?? null })}
            ariaLabel={t({ en: 'Client', nb: 'Klient' })}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>{t({ en: 'Status', nb: 'Status' })}</FieldLabel>
          <Fieldset
            render={
              <RadioGroup
                required
                value={entry.isWon ?? ''}
                onValueChange={(v) => onUpdate({ isWon: v as never })}
              />
            }
          >
            {statusOptions.map((option) => (
              <FieldItem key={option.value}>
                <RadioGroupLabel>
                  <RadioGroupItem value={option.value} required />
                  <RadioGroupItemText>
                    <RadioGroupItemTitle>{option.label}</RadioGroupItemTitle>
                  </RadioGroupItemText>
                </RadioGroupLabel>
              </FieldItem>
            ))}
          </Fieldset>
          <FieldError />
        </Field>
        <div className="space-y-4 border-t pt-4">
          {FILE_FIELDS.map(({ key, label }) => (
            <Field key={key}>
              <FieldLabel>{t(label)}</FieldLabel>
              <FileDropzone
                value={entry[key]}
                onValueChange={(files) => onUpdate({ [key]: files })}
                disabled={isSubmitting}
              />
              {entry[key].length > 0 && (
                <FileTable className="mt-4">
                  {entry[key].map((file, idx) => (
                    <FileRow
                      key={idx}
                      file={file}
                      disabled={isSubmitting}
                      onRemove={() => onUpdate({ [key]: entry[key].filter((f) => f !== file) })}
                    />
                  ))}
                </FileTable>
              )}
            </Field>
          ))}
          <Field>
            <FieldLabel>{t({ en: 'Written feedback', nb: 'Skriftlig tilbakemelding' })}</FieldLabel>
            <Textarea
              minRows={3}
              placeholder={t({
                en: 'Enter feedback as free text',
                nb: 'Skriv inn tilbakemelding som fritekst',
              })}
              value={entry.feedbackText}
              onChange={(e) => onUpdate({ feedbackText: e.target.value })}
              disabled={isSubmitting}
            />
            <FieldDescription>
              {t({
                en: 'Optionally type feedback as free text instead of, or in addition to, uploading feedback documents.',
                nb: 'Skriv eventuelt tilbakemelding som fritekst i stedet for, eller i tillegg til, å laste opp tilbakemeldingsdokumenter.',
              })}
            </FieldDescription>
          </Field>
        </div>
      </div>
    </div>
  );
}
